/**
 * KARMA ÖDEME (Parçalı Ödeme) Hook
 * POS + Nakit karma ödemeler için özel sistem
 * 
 * İŞLEM AKIŞI:
 * 1. Önce POS işlemi (yazarkasa + SQL)
 * 2. POS başarılıysa Nakit işlemi (yazarkasa + SQL)
 * 3. Her iki işlem de başarılıysa stok düşer
 * 4. Cari hareketi tek, ama açıklamada her iki ödeme tipi belirtilir
 */

import { useState } from 'react';
import { CartItem, InventoryItem as QuickSaleInventoryItem } from '../QuickSaleScreen';
import salesService, { QuickSalePartialWithInvoiceRequest } from '../../../../services/salesService';
import { useAuth } from '../../../../contexts/AuthContext';
import accountingService from '../../../../services/accountingService';
import { getPavoCardMediator } from '../../../../services/pavoMediatorConfig';
import { getFiscalDevice, isFiscalDeviceSupported, getFiscalDeviceDisplayName } from './fiscalDevices';
import type { FiscalDeviceType, FiscalProductItem } from './fiscalDevices/types';
import { syncDepartmentVatMapping, type DepartmentVatSyncResult } from './fiscalDevices/ingenico';
import { resolveWarehouseId } from '../utils/resolveWarehouseId';
import { parseVatRate } from '../utils/vatTaxGroup';
import { 
  startSaleWithItems, 
  addPaymentToSale, 
  finalizePavoPaySale, 
  abandonPavoPaySale,
  savePavoPaySale 
} from './fiscalDevices/pavopay';
import { accessLockCode, accessLockMessage, clearPendingAtomicPayment } from '../utils/accessLockError';

interface UsePartialPaymentProps {
  cart: CartItem[];
  companyId: string | null;
  companyName?: string | null; // ✅ YENİ: QuickSaleScreen'den cache'lenmiş şirket adı
  selectedCustomer: any;
  allInventoryItems: QuickSaleInventoryItem[];
  userDetails: any;
  currentUserDisplayName: string | null;
  cashPaymentCashboxId: string | null;
  cashPaymentCashboxName?: string | null; // ✅ YENİ: Kasa adı (gün sonu raporu için)
  posOptions: any[];
  showAlertModal: (title: string, message: string, showQuickAdd?: boolean, onRetry?: (() => void) | null) => void;
  showSnackbar: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  clearCart: () => void;
  calculateTotal: () => number;
  screenId?: string | null;
  screenSettings?: any;
  selectedFiscalDevice?: FiscalDeviceType;
  setPaymentWaitingModalOpen?: (open: boolean) => void;
  setPaymentWaitingStatus?: (status: 'waiting' | 'success' | 'error') => void;
  setPaymentWaitingMessage?: (message: string) => void;
  setIsProcessingPosPayment?: (processing: boolean) => void; // ✅ YENİ: POS modal kontrolü
  printReceiptAfterPayment?: (invoiceNumber: string, paymentType: string, items: any[], remainingBalance?: number, kitchenShortCode?: string | null) => Promise<void>;
  // ✅ YENİ: Restoran modu desteği
  isRestaurantMode?: boolean;
  tableId?: string | null;
  tableName?: string | null;
  restaurantScreenId?: number | null;
  waiterName?: string | null;
  navigateToRestaurantScreen?: () => void;
  // ✅ YENİ: Kasiyer bilgileri (gün sonu raporu için)
  currentCashierId?: number | null;
  currentCashierName?: string | null;
  // ✅ YENİ: Şube ID (Branch bazında benzersiz sipariş numarası için)
  activeBranchId?: string | null;
  // Sipariş numarası rezervasyonu
  reservedLongCode?: string | null;
  reservedShortCode?: string | null;
  // ✅ Ingenico departman-KDV uyumsuzluğu (karma ödeme için - ödeme DURUR)
  onIngenicoDeptVatMismatch?: (syncResult: DepartmentVatSyncResult) => void;
  // ✅ YENİ: Nakit parçası başarıyla kaydedildikten sonra çağrılır (para çekmecesi tetiği için)
  onCashPaymentSuccess?: (paidByCash: number) => void;
}

export interface PartialPaymentDetails {
  cashAmount: number;
  posAmount: number;
  posOption: {
    id: string;
    label: string;
    bankAccountId: number;
    subAccountId: number | null;
    bankAccountName?: string | null; // ✅ YENİ: Banka adı (gün sonu raporu için)
  };
}

export const usePartialPayment = ({
  cart,
  companyId,
  companyName, // ✅ YENİ: Cache'lenmiş şirket adı
  selectedCustomer,
  allInventoryItems,
  userDetails,
  currentUserDisplayName,
  cashPaymentCashboxId,
  cashPaymentCashboxName = null, // ✅ YENİ: Kasa adı
  posOptions,
  showAlertModal,
  showSnackbar,
  clearCart,
  calculateTotal,
  screenId = null,
  screenSettings,
  selectedFiscalDevice = 'hugin_t300',
  setPaymentWaitingModalOpen,
  setPaymentWaitingStatus,
  setPaymentWaitingMessage,
  setIsProcessingPosPayment, // ✅ YENİ: POS modal kontrolü
  printReceiptAfterPayment, // ✅ YENİ: Fiş yazdırma
  // ✅ YENİ: Restoran modu desteği
  isRestaurantMode = false,
  tableId = null,
  tableName = null,
  restaurantScreenId = null,
  waiterName = null,
  navigateToRestaurantScreen,
  // ✅ YENİ: Kasiyer bilgileri
  currentCashierId = null,
  currentCashierName = null,
  // ✅ YENİ: Şube ID
  activeBranchId = null,
  // Sipariş numarası
  reservedLongCode = null,
  reservedShortCode = null,
  onIngenicoDeptVatMismatch,
  onCashPaymentSuccess,
}: UsePartialPaymentProps) => {
  // ✅ YENİ: AuthContext'ten şube adı alma
  const { getBranchName } = useAuth();
  
  // ✅ YENİ: Kasiyer sistemi aktifse kasiyer adı, yoksa kullanıcı adı kullan
  // Bu değer tüm CreatedBy ve completedByName alanlarında kullanılır
  const effectiveCreatedBy = currentCashierName || currentUserDisplayName || userDetails?.email || 'System';
  
  const [isProcessing, setIsProcessing] = useState(false);
  
  // ✅ YENİ: Yazarkasa bağlı değil onay modalı için state'ler
  const [isFiscalDisconnectedConfirmOpenPartial, setIsFiscalDisconnectedConfirmOpenPartial] = useState(false);
  const [pendingPartialPaymentCallback, setPendingPartialPaymentCallback] = useState<(() => void) | null>(null);

  // Yazarkasa cihazı
  const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
  const isFiscalDeviceActive = isFiscalDeviceSupported(selectedFiscalDevice);
  const fiscalDeviceName = getFiscalDeviceDisplayName(selectedFiscalDevice);

  /**
   * KARMA ÖDEME İŞLEMİ
   * Önce POS, sonra Nakit
   * @param skipFiscalDevice Yazarkasa atlanacak mı? (Kullanıcı onayladıysa true)
   * @param skipDeptVatCheck Ingenico departman-KDV kontrolü atlanacak mı? ("Devam Et" seçildiyse true)
   */
  const processPartialPayment = async (paymentDetails: PartialPaymentDetails, skipFiscalDevice: boolean = false, skipDeptVatCheck: boolean = false) => {
    const startTime = performance.now();
    console.log('[KARMA ÖDEME] Başlıyor...', paymentDetails, `skipFiscalDevice: ${skipFiscalDevice}`, `skipDeptVatCheck: ${skipDeptVatCheck}`);

    // ✅ Harici POS: entegre yazarkasa yok — tüm fiscal adımlar atlanır,
    // karma ödeme yalnızca SQL kayıtlarıyla ilerler.
    if (selectedFiscalDevice === 'harici_pos' && !skipFiscalDevice) {
      console.log('[KARMA ÖDEME] ℹ️ Harici POS seçili — yazarkasa adımları atlanıyor.');
      skipFiscalDevice = true;
    }

    if (isProcessing) return;
    setIsProcessing(true);

    // Validasyon
    if (cart.length === 0) {
      showAlertModal('Uyarı', 'Ödeme yapmak için sepette ürün olmalıdır.');
      setIsProcessing(false);
      return;
    }
    if (!companyId) {
      showAlertModal('Hata', 'Şirket bilgisi bulunamadı.');
      setIsProcessing(false);
      return;
    }
    if (!selectedCustomer) {
      showAlertModal('Hata', 'Cari hesap seçilmedi.');
      setIsProcessing(false);
      return;
    }
    if (!cashPaymentCashboxId) {
      showAlertModal('Hata', 'Nakit kasa atanmamış.');
      setIsProcessing(false);
      return;
    }

    // ✅ Yazarkasa kontrolü - skipFiscalDevice false ise kontrol et
    if (!skipFiscalDevice) {
      if (!isFiscalDeviceActive) {
        showAlertModal(
          'Yazarkasa Entegrasyonu Mevcut Değil',
          `Seçili yazarkasa (${fiscalDeviceName}) için entegrasyon henüz geliştirilmemiştir.\n\n` +
            `Lütfen Ayarlar > Ödeme Yöntemleri bölümünden "Hugin T300" yazarkasasını seçiniz.`
        );
        setIsProcessing(false);
        return;
      }

      // ✅ YENİ: Yazarkasa bağlantısını kontrol et
      console.log(`[KARMA ÖDEME] 🔌 Yazarkasa bağlantısı kontrol ediliyor...`);
      let fiscalDeviceConnectedForPos = false;
      try {
        fiscalDeviceConnectedForPos = fiscalDevice.checkConnection ? await fiscalDevice.checkConnection() : false;
        console.log(`[KARMA ÖDEME] 🔌 POS için bağlantı durumu: ${fiscalDeviceConnectedForPos ? 'BAĞLI ✅' : 'BAĞLI DEĞİL ❌'}`);
      } catch (connError) {
        console.error(`[KARMA ÖDEME] 🔌 POS bağlantı kontrolü hatası:`, connError);
        fiscalDeviceConnectedForPos = false;
      }

      // ✅ Yazarkasa BAĞLI DEĞİLSE → Onay modalı göster
      if (!fiscalDeviceConnectedForPos) {
        console.log(`[KARMA ÖDEME] ⚠️ Yazarkasa bağlı değil! Onay modalı gösteriliyor...`);
        
        // Callback'i kaydet - kullanıcı onaylarsa skipFiscalDevice=true ile tekrar çağrılacak
        setPendingPartialPaymentCallback(() => async () => {
          console.log(`[KARMA ÖDEME] 👤 Kullanıcı onayladı, yazarkasa OLMADAN devam ediliyor...`);
          await processPartialPayment(paymentDetails, true);
        });
        
        // Onay modalını aç
        setIsFiscalDisconnectedConfirmOpenPartial(true);
        
        setIsProcessing(false);
        return;
      }

      // Departman numarası kontrolü (sadece yazarkasa kullanılacaksa)
      console.log('[KARMA ÖDEME] Departman numaraları kontrol ediliyor...');
      const productsWithoutDeptNo = [];
      for (const cartItem of cart) {
        const product = allInventoryItems.find((inv) => inv.id === cartItem.id);
        if (!product?.departmentNo || product.departmentNo < 1 || product.departmentNo > 100) {
          productsWithoutDeptNo.push(product?.name || 'Bilinmeyen Ürün');
        }
      }

      if (productsWithoutDeptNo.length > 0) {
        showAlertModal(
          'Departman Numarası Eksik',
          `Aşağıdaki ürünler için departman numarası (1-100 arası) tanımlanmamış:\n\n` +
            productsWithoutDeptNo.map((name) => `• ${name}`).join('\n') +
            `\n\nLütfen ürün ayarlarından departman numarası ekleyiniz.`
        );
        setIsProcessing(false);
        return;
      }

      // ✅ Ingenico 5000F: Ödeme öncesi departman-KDV uyumluluğu kontrolü
      if (selectedFiscalDevice === 'ingenico_5000f' && !skipDeptVatCheck) {
        const syncProducts = cart.map((cartItem) => {
          const inv = allInventoryItems.find((item) => item.id === cartItem.id);
          return {
            departmentNo: (inv?.departmentNo ?? 0) as number,
            vatRate: inv?.vatRate ?? 0,
            name: cartItem.name || inv?.name || '',
          };
        }).filter((p) => p.departmentNo >= 1);
        if (syncProducts.length > 0) {
          const syncResult = await syncDepartmentVatMapping(syncProducts);
          if (syncResult.hasMismatches) {
            console.warn('[KARMA ÖDEME] Ingenico departman-KDV uyumsuzluğu tespit edildi.');
            if (onIngenicoDeptVatMismatch) {
              onIngenicoDeptVatMismatch(syncResult);
            } else {
              showAlertModal(
                'Departman-KDV Uyumsuzluğu',
                'Yazarkasadaki departman KDV oranları ürünlerinizle eşleşmiyor. Lütfen ayarlardan "KDV/Departman Tablosu" ile kontrol edin.'
              );
            }
            setIsProcessing(false);
            return;
          }
        }
      }
    } else {
      console.log('[KARMA ÖDEME] ⚠️ Yazarkasa atlandı (kullanıcı onayı ile)');
    }

    let atomicRequest: QuickSalePartialWithInvoiceRequest | undefined;

    try {
      let posReferenceNumber = '';
      let posFiscalData: any = null;

      // ✅ PavoPay için özel akış (StartSaleWithItems → AddPayment → AddPayment → FinalizeSale)
      const isPavoPay = selectedFiscalDevice === 'pavopay';
      
      if (isPavoPay && !skipFiscalDevice) {
        console.log('[KARMA ÖDEME] 🔄 PavoPay parçalı ödeme akışı başlıyor...');
        
        // ✅ POS Modal'ı aç
        if (setIsProcessingPosPayment) {
          setIsProcessingPosPayment(true);
        }
        
        try {
          // 1️⃣ ADIM: StartSaleWithItems - Satışı başlat
          const totalAmount = calculateTotal();
          const products = cart.map((cartItem) => {
            const product = allInventoryItems.find((inv) => inv.id === cartItem.id);
            // Stok kartı esastır; bulunamazsa sepet kalemindeki oran kullanılır.
            // Eskiden `|| 0` vardı: oran çözülemediğinde fiş sessizce KDV'siz
            // kesiliyordu (restoran masası kalemleri oranı taşımadığı için sık görülüyordu).
            const vatRate = cartItem.id.startsWith('DEPOSIT-')
              ? 20
              : parseVatRate(product?.vatRate ?? cartItem.vatRate);
            if (vatRate === undefined) {
              throw new Error(
                `"${cartItem.name}" ürününün KDV oranı belirlenemedi. Fişin KDV'siz kesilmemesi ` +
                `için işlem durduruldu. Lütfen ürünün stok kartındaki KDV oranını kontrol edin.`
              );
            }

            
            // ✅ FIX: Restoran modunda fiyatlar zaten KDV dahil
            let unitPrice: number;
            if (isRestaurantMode) {
              // RESTORAN MODU: Fiyat zaten KDV dahil, olduğu gibi gönder
              unitPrice = cartItem.price;
            } else {
              // NORMAL HIZLI SATIŞ: KDV hariç fiyat, KDV ekle
              unitPrice = cartItem.price * (1 + (vatRate / 100));
            }
            
            return {
              name: cartItem.name,
              quantity: cartItem.quantity,
              unitPrice, // ✅ Artık mode'a göre doğru fiyat
              vatRate,
              departmentNo: product?.departmentNo || 1,
            };
          });
          
          const useKitchenScreenForPartial = screenSettings?.useKitchenScreen || false;
          const kitchenOrderCodeForPartial = (useKitchenScreenForPartial && reservedShortCode) 
            ? reservedShortCode 
            : undefined;
          const receiptCompanyNameForPartial = useKitchenScreenForPartial 
            ? (screenSettings?.receiptDisplaySettings?.companyName || companyName || undefined)
            : undefined;
          const useExternalPrinterForPartial = screenSettings?.useExternalPrinterForPavo || false;

          console.log('[PavoPay KARMA] 1/4: StartSaleWithItems başlıyor...', { isRestaurantMode, totalAmount, productCount: products.length, kitchenOrderCodeForPartial, useExternalPrinterForPartial });
          const startResult = await startSaleWithItems(products, totalAmount, companyId!, isRestaurantMode, kitchenOrderCodeForPartial, receiptCompanyNameForPartial, useExternalPrinterForPartial);
          
          if (!startResult.success || !startResult.saleNumber) {
            throw new Error(`Satış başlatılamadı: ${startResult.errorMessage}`);
          }
          
          const saleNumber = startResult.saleNumber;
          // PavoPay'in kabul ettiği gerçek toplam (ürün fiyatları yuvarlandıktan sonra hesaplanan)
          const pavoPayActualTotal = startResult.totalAmount || totalAmount;
          console.log(`[PavoPay KARMA] ✅ Satış başlatıldı: ${saleNumber}, PavoPay Toplam: ${pavoPayActualTotal}`);
          
          // 2️⃣ ADIM: AddPayment - POS ödemesi ekle
          console.log(`[PavoPay KARMA] 2/4: POS ödemesi ekleniyor (${paymentDetails.posAmount} TL)...`);
          const posPaymentResult = await addPaymentToSale(
            saleNumber,
            paymentDetails.posAmount,
            getPavoCardMediator(), // Kart aracısı ekran ayarından (varsayılan 2 = BKM/TechPOS)
            pavoPayActualTotal
          );
          
          if (!posPaymentResult.success) {
            console.error('[PavoPay KARMA] ❌ POS ödemesi başarısız:', posPaymentResult.errorMessage);
            // Satıştan vazgeç
            await abandonPavoPaySale(saleNumber);
            throw new Error(`POS ödemesi alınamadı: ${posPaymentResult.errorMessage}`);
          }
          
          console.log(`[PavoPay KARMA] ✅ POS ödemesi eklendi`);
          posFiscalData = posPaymentResult.data;
          
          // 3️⃣ ADIM: AddPayment - Nakit ödemesi ekle
          console.log(`[PavoPay KARMA] 3/4: Nakit ödemesi ekleniyor (${paymentDetails.cashAmount} TL)...`);
          const cashPaymentResult = await addPaymentToSale(
            saleNumber,
            paymentDetails.cashAmount,
            1, // Mediator 1 = Nakit
            pavoPayActualTotal
          );
          
          if (!cashPaymentResult.success) {
            console.error('[PavoPay KARMA] ❌ Nakit ödemesi başarısız:', cashPaymentResult.errorMessage);
            await abandonPavoPaySale(saleNumber);
            throw new Error(`Nakit ödemesi alınamadı: ${cashPaymentResult.errorMessage}`);
          }
          
          console.log(`[PavoPay KARMA] ✅ Nakit ödemesi eklendi`);
          
          // 4️⃣ ADIM: FinalizeSale - Satışı tamamla
          console.log('[PavoPay KARMA] 4/4: Satış tamamlanıyor...');
          const finalizeResult = await finalizePavoPaySale(
            saleNumber,
            companyId!,
            pavoPayActualTotal,
            cart.map(item => ({ name: item.name, quantity: item.quantity, price: item.price })),
            kitchenOrderCodeForPartial,
            receiptCompanyNameForPartial,
            useExternalPrinterForPartial
          );
          
          if (!finalizeResult.success) {
            console.error('[PavoPay KARMA] ❌ Satış tamamlanamadı:', finalizeResult.errorMessage);
            throw new Error(`Satış tamamlanamadı: ${finalizeResult.errorMessage}`);
          }
          
          console.log(`[PavoPay KARMA] ✅ Satış tamamlandı: ${saleNumber}`);
          
          // POS modal'ı kapat
          if (setIsProcessingPosPayment) {
            setIsProcessingPosPayment(false);
          }
          
        } catch (pavoPayError: any) {
          // POS modal'ı kapat
          if (setIsProcessingPosPayment) {
            setIsProcessingPosPayment(false);
          }
          throw pavoPayError;
        }
        
        // PavoPay için ayrı yazarkasa işlemi yapmayız, hepsi tek satışta
        // Doğrudan SQL atomik işleme geç...
        
      } else {
        // ==================== ADIM 1: POS ÖDEME (Hugin/Inpos vs.) ====================
        console.log(`[KARMA ÖDEME] 1/2: POS ödeme başlıyor (${paymentDetails.posAmount} TL)...`);

      // ✅ POS Modal'ı aç
      if (setIsProcessingPosPayment) {
        setIsProcessingPosPayment(true);
      }

      let posPaymentSuccess = false;

      // ✅ Ürünleri FiscalProductItem formatına çevir ve FİYATLARI ORANTıLı BÖL
      // ÖNEMLI: Karma ödemede her ödeme için ayrı belge kesilir
      // POS belgesi sadece POS tutarı kadar olmalı
      const totalCartAmount = calculateTotal(); // KDV DAHİL
      console.log(`[KARMA POS] Sepet Toplam Tutarı: ${totalCartAmount.toFixed(2)} TL (KDV DAHİL)`);
      console.log(`[KARMA POS] POS Ödeme Tutarı: ${paymentDetails.posAmount.toFixed(2)} TL`);
      
      const posRatio = paymentDetails.posAmount / totalCartAmount;
      console.log(`[KARMA POS] POS Oranı: ${(posRatio * 100).toFixed(2)}%`);

      // ✅ YENİ: Yazarkasa işlemi - skipFiscalDevice false ise yap
      if (!skipFiscalDevice) {
        // ⚠️ PavoPay KDV DAHİL fiyat bekler, Inpos/Hugin KDV HARİÇ fiyat bekler
        const isPavoPay = selectedFiscalDevice === 'pavopay';
        
        const fiscalProducts: FiscalProductItem[] = cart.map((cartItem) => {
          const product = allInventoryItems.find((inv) => inv.id === cartItem.id);
          if (!product) {
            throw new Error(`Ürün bulunamadı: ${cartItem.id}`);
          }

          const vatRate = product.vatRate || 0;
          const isVatIncluded = cartItem.vatStatus === 'included';
          
          // Orantılı fiyat hesabı (sepet fiyatı üzerinden)
          const proportionalPrice = cartItem.price * posRatio;
          
          // Yazarkasaya göre son fiyatı belirle
          let fiscalPrice: number;
          
          if (isPavoPay) {
            // ✅ PavoPay KDV DAHİL fiyat bekliyor
            if (isVatIncluded) {
              // Fiyat zaten KDV dahil, olduğu gibi gönder
              fiscalPrice = proportionalPrice;
              console.log(`[KARMA POS-PAVOPAY] ${cartItem.name}: ${fiscalPrice.toFixed(2)} TL (KDV dahil, olduğu gibi)`);
            } else {
              // Fiyat KDV hariç, KDV ekle
              fiscalPrice = proportionalPrice * (1 + vatRate / 100);
              console.log(`[KARMA POS-PAVOPAY] ${cartItem.name}: ${proportionalPrice.toFixed(2)} TL (KDV hariç) → ${fiscalPrice.toFixed(2)} TL (KDV dahil)`);
            }
          } else {
            // ✅ Inpos/Hugin KDV HARİÇ fiyat bekliyor
            if (isVatIncluded) {
              // Fiyat KDV dahil geliyor, KDV'den arındır (yazarkasa tekrar ekleyecek)
              fiscalPrice = proportionalPrice / (1 + vatRate / 100);
              console.log(`[KARMA POS-INPOS/HUGIN] ${cartItem.name}: ${proportionalPrice.toFixed(2)} TL (KDV dahil) → ${fiscalPrice.toFixed(2)} TL (KDV hariç)`);
            } else {
              // Fiyat zaten KDV hariç, olduğu gibi gönder
              fiscalPrice = proportionalPrice;
              console.log(`[KARMA POS-INPOS/HUGIN] ${cartItem.name}: ${fiscalPrice.toFixed(2)} TL (KDV hariç, olduğu gibi)`);
            }
          }

          return {
            id: cartItem.id,
            name: cartItem.name,
            quantity: cartItem.quantity,
            price: fiscalPrice,
            vatRate: vatRate,
            departmentNo: product.departmentNo!,
            barcode: product.barcode || '',
          };
        });

        // ✅ INPOS İÇİN ÖZEL TOPLAM HESAPLAMA (KARMA ÖDEME - POS)
        // Inpos kendi sepet toplamını kuruş cinsinden hesaplıyor
        // Yuvarlama farklılıklarını önlemek için aynı yöntemi kullanıyoruz
        let fiscalTotalForPos: number;
        
        if (selectedFiscalDevice === 'inpos_m530') {
          // ✅ INPOS: Her ürün için KDV dahil fiyatı kuruşa çevir ve topla
          let inposTotalKurus = 0;
          const expectedPosKurus = Math.round(paymentDetails.posAmount * 100);
          
          console.log(`[KARMA POS-INPOS] 📊 İNDİRİM ANALİZİ - Beklenen POS: ${paymentDetails.posAmount.toFixed(2)} TL (${expectedPosKurus} kuruş)`);
          
          for (const fp of fiscalProducts) {
            const priceWithVat = fp.price * (1 + fp.vatRate / 100);
            const unitPriceKurus = Math.round(priceWithVat * 100);
            const itemTotalKurus = unitPriceKurus * fp.quantity;
            inposTotalKurus += itemTotalKurus;
            
            console.log(`[KARMA POS-INPOS] 📊 ${fp.name}: ${fp.price.toFixed(4)} → ${priceWithVat.toFixed(4)} → ${unitPriceKurus} kuruş x ${fp.quantity} = ${itemTotalKurus}`);
          }
          
          const farkKurus = expectedPosKurus - inposTotalKurus;
          console.log(`[KARMA POS-INPOS] 📊 Hesaplanan: ${inposTotalKurus} kuruş, Beklenen: ${expectedPosKurus} kuruş, FARK: ${farkKurus} kuruş`);
          
          // ✅ DÜZELTME: Fark varsa ve küçükse, toplama ekle
          if (farkKurus !== 0 && Math.abs(farkKurus) <= 50) {
            console.log(`[KARMA POS-INPOS] 🔧 Yuvarlama farkı düzeltiliyor: ${farkKurus} kuruş`);
            inposTotalKurus += farkKurus;
          }
          
          fiscalTotalForPos = inposTotalKurus / 100;
          console.log(`[KARMA POS-INPOS] 🔢 Inpos toplam hesaplandı: ${inposTotalKurus} kuruş = ${fiscalTotalForPos.toFixed(2)} TL`);
        } else {
          // ✅ DİĞER CİHAZLAR: Normal POS tutarını kullan
          fiscalTotalForPos = paymentDetails.posAmount;
          console.log(`[KARMA POS-${selectedFiscalDevice?.toUpperCase()}] 🔢 Normal POS tutarı: ${fiscalTotalForPos.toFixed(2)} TL`);
        }

        // POS yazarkasa işlemi - AYRI TRY-CATCH İLE HATA YAKALAMA (usePayment.ts ile aynı pattern)
        try {
          const useKitchenScreenForPartialOther = screenSettings?.useKitchenScreen || false;
          const kitchenOrderCodeForPartialPos = (selectedFiscalDevice === 'pavopay' && useKitchenScreenForPartialOther && reservedShortCode) 
            ? reservedShortCode 
            : undefined;
          const receiptCompanyNameForPartialPos = (selectedFiscalDevice === 'pavopay' && useKitchenScreenForPartialOther) 
            ? (screenSettings?.receiptDisplaySettings?.companyName || companyName || undefined)
            : undefined;
          const useExternalPrinterForPartialPos = screenSettings?.useExternalPrinterForPavo || false;
          const posResult = await fiscalDevice.processPosPayment(
            fiscalProducts,
            fiscalTotalForPos,
            showAlertModal,
            undefined,
            undefined,
            kitchenOrderCodeForPartialPos,
            receiptCompanyNameForPartialPos,
            useExternalPrinterForPartialPos
          );

          if (!posResult.success) {
            throw new Error(posResult.errorMessage || 'POS yazarkasa işlemi başarısız oldu');
          }

          posFiscalData = posResult.fiscalData || null;
          posPaymentSuccess = true;
          console.log(`[KARMA ÖDEME] ✅ POS yazarkasa başarılı. Belge ID: ${posResult.documentId}`);
        } catch (posFiscalError: any) {
          const posErrorMessage = posFiscalError.message || 'Bilinmeyen POS hatası';
          console.error(`[KARMA ÖDEME] ❌ POS yazarkasa hatası:`, posErrorMessage);

          if (setIsProcessingPosPayment) {
            setIsProcessingPosPayment(false);
          }

          showAlertModal(
            '❌ POS Ödeme Hatası',
            `POS ödeme işlemi sırasında bir hata oluştu.\n\n${posErrorMessage}\n\nÖdeme alınamadı. Lütfen tekrar deneyin.`
          );

          setIsProcessing(false);
          return;
        }
      } else {
        console.log(`[KARMA ÖDEME] ⚠️ POS yazarkasa işlemi ATLANDI (kullanıcı onayı ile)`);
        posPaymentSuccess = true;
      }

      // ✅ POS yazarkasa bitti, modal'ı kapat
      if (setIsProcessingPosPayment) {
        setIsProcessingPosPayment(false);
      }

      if (!posPaymentSuccess) {
        console.error('[KARMA ÖDEME] ❌ POS ödeme başarısız, SQL işlemine geçilmeyecek.');
        setIsProcessing(false);
        return;
      }

      // ==================== ADIM 2: NAKİT YAZARKASA (VAR İSE) ====================
      console.log(`[KARMA ÖDEME] 2/3: Nakit yazarkasa kontrolü (${paymentDetails.cashAmount} TL)...`);

      const useFiscalDeviceForCash = screenSettings?.useFiscalDeviceForCash ?? true;
      console.log(`[KARMA ÖDEME] NAKİT için ÖKC Kullanımı: ${useFiscalDeviceForCash ? 'AÇIK ✅' : 'KAPALI ⚠️'}`);

      let cashFiscalDocumentId: string | undefined = undefined;

      if (useFiscalDeviceForCash && !skipFiscalDevice) {
        let fiscalDeviceConnectedForCash = false;
        try {
          fiscalDeviceConnectedForCash = fiscalDevice.checkConnection ? await fiscalDevice.checkConnection() : false;
        } catch (connError) {
          fiscalDeviceConnectedForCash = false;
        }

        if (fiscalDeviceConnectedForCash) {
          console.log(`[KARMA ÖDEME] Nakit için yazarkasa işlemi başlatılıyor...`);

          const cashRatio = paymentDetails.cashAmount / totalCartAmount;
          // ⚠️ PavoPay KDV DAHİL fiyat bekler, Inpos/Hugin KDV HARİÇ fiyat bekler
          const isPavoPayCash = selectedFiscalDevice === 'pavopay';
          
          const fiscalProductsForCash: FiscalProductItem[] = cart.map((cartItem) => {
            const product = allInventoryItems.find((inv) => inv.id === cartItem.id);
            if (!product) throw new Error(`Ürün bulunamadı: ${cartItem.id}`);

            const vatRate = product.vatRate || 0;
            const isVatIncluded = cartItem.vatStatus === 'included';
            
            // Orantılı fiyat hesabı (sepet fiyatı üzerinden)
            const proportionalPrice = cartItem.price * cashRatio;
            
            // Yazarkasaya göre son fiyatı belirle
            let fiscalPrice: number;
            
            if (isPavoPayCash) {
              // ✅ PavoPay KDV DAHİL fiyat bekliyor
              if (isVatIncluded) {
                // Fiyat zaten KDV dahil, olduğu gibi gönder
                fiscalPrice = proportionalPrice;
                console.log(`[KARMA NAKİT-PAVOPAY] ${cartItem.name}: ${fiscalPrice.toFixed(2)} TL (KDV dahil, olduğu gibi)`);
              } else {
                // Fiyat KDV hariç, KDV ekle
                fiscalPrice = proportionalPrice * (1 + vatRate / 100);
                console.log(`[KARMA NAKİT-PAVOPAY] ${cartItem.name}: ${proportionalPrice.toFixed(2)} TL (KDV hariç) → ${fiscalPrice.toFixed(2)} TL (KDV dahil)`);
              }
            } else {
              // ✅ Inpos/Hugin KDV HARİÇ fiyat bekliyor
              if (isVatIncluded) {
                // Fiyat KDV dahil geliyor, KDV'den arındır (yazarkasa tekrar ekleyecek)
                fiscalPrice = proportionalPrice / (1 + vatRate / 100);
                console.log(`[KARMA NAKİT-INPOS/HUGIN] ${cartItem.name}: ${proportionalPrice.toFixed(2)} TL (KDV dahil) → ${fiscalPrice.toFixed(2)} TL (KDV hariç)`);
              } else {
                // Fiyat zaten KDV hariç, olduğu gibi gönder
                fiscalPrice = proportionalPrice;
                console.log(`[KARMA NAKİT-INPOS/HUGIN] ${cartItem.name}: ${fiscalPrice.toFixed(2)} TL (KDV hariç, olduğu gibi)`);
              }
            }

            return {
              id: cartItem.id,
              name: cartItem.name,
              quantity: cartItem.quantity,
              price: fiscalPrice,
              vatRate: vatRate,
              departmentNo: product.departmentNo!,
              barcode: product.barcode || '',
            };
          });

          // ✅ INPOS İÇİN ÖZEL TOPLAM HESAPLAMA (KARMA ÖDEME - NAKİT)
          let fiscalTotalForCash: number;
          
          if (selectedFiscalDevice === 'inpos_m530') {
            // ✅ INPOS: Her ürün için KDV dahil fiyatı kuruşa çevir ve topla
            let inposTotalKurus = 0;
            const expectedCashKurus = Math.round(paymentDetails.cashAmount * 100);
            
            console.log(`[KARMA NAKİT-INPOS] 📊 İNDİRİM ANALİZİ - Beklenen Nakit: ${paymentDetails.cashAmount.toFixed(2)} TL (${expectedCashKurus} kuruş)`);
            
            for (const fp of fiscalProductsForCash) {
              const priceWithVat = fp.price * (1 + fp.vatRate / 100);
              const unitPriceKurus = Math.round(priceWithVat * 100);
              const itemTotalKurus = unitPriceKurus * fp.quantity;
              inposTotalKurus += itemTotalKurus;
              
              console.log(`[KARMA NAKİT-INPOS] 📊 ${fp.name}: ${fp.price.toFixed(4)} → ${priceWithVat.toFixed(4)} → ${unitPriceKurus} kuruş x ${fp.quantity} = ${itemTotalKurus}`);
            }
            
            const farkKurus = expectedCashKurus - inposTotalKurus;
            console.log(`[KARMA NAKİT-INPOS] 📊 Hesaplanan: ${inposTotalKurus} kuruş, Beklenen: ${expectedCashKurus} kuruş, FARK: ${farkKurus} kuruş`);
            
            // ✅ DÜZELTME: Fark varsa ve küçükse, toplama ekle
            if (farkKurus !== 0 && Math.abs(farkKurus) <= 50) {
              console.log(`[KARMA NAKİT-INPOS] 🔧 Yuvarlama farkı düzeltiliyor: ${farkKurus} kuruş`);
              inposTotalKurus += farkKurus;
            }
            
            fiscalTotalForCash = inposTotalKurus / 100;
            console.log(`[KARMA NAKİT-INPOS] 🔢 Inpos toplam hesaplandı: ${inposTotalKurus} kuruş = ${fiscalTotalForCash.toFixed(2)} TL`);
          } else {
            // ✅ DİĞER CİHAZLAR: Normal Nakit tutarını kullan
            fiscalTotalForCash = paymentDetails.cashAmount;
            console.log(`[KARMA NAKİT-${selectedFiscalDevice?.toUpperCase()}] 🔢 Normal Nakit tutarı: ${fiscalTotalForCash.toFixed(2)} TL`);
          }

          const useKitchenScreenForPartialCash = screenSettings?.useKitchenScreen || false;
          const kitchenOrderCodeForPartialCash = (selectedFiscalDevice === 'pavopay' && useKitchenScreenForPartialCash && reservedShortCode) 
            ? reservedShortCode 
            : undefined;
          const receiptCompanyNameForPartialCash = (selectedFiscalDevice === 'pavopay' && useKitchenScreenForPartialCash) 
            ? (screenSettings?.receiptDisplaySettings?.companyName || companyName || undefined)
            : undefined;
          const useExternalPrinterForPartialCash = screenSettings?.useExternalPrinterForPavo || false;
          const cashResult = await fiscalDevice.processCashPayment(
            fiscalProductsForCash,
            fiscalTotalForCash,
            showAlertModal,
            kitchenOrderCodeForPartialCash,
            receiptCompanyNameForPartialCash,
            useExternalPrinterForPartialCash
          );

          if (!cashResult.success) {
            throw new Error(`Nakit yazarkasa işlemi başarısız: ${cashResult.errorMessage}`);
          }

          cashFiscalDocumentId = cashResult.documentId;
          console.log(`[KARMA ÖDEME] ✅ Nakit yazarkasa başarılı. Belge ID: ${cashFiscalDocumentId}`);
        }
      }
      } // ✅ PavoPay else bloğunu kapat

      // ==================== ADIM 3: ATOMİK SQL (POS + NAKİT + STOK + FATURA) ====================
      console.log(`[KARMA ÖDEME] 3/3: ATOMİK SQL işlemi başlıyor (tüm işlemler tek transaction)...`);
      console.log('🏢 [ŞİRKET] Cache\'den alınan şirket adı:', companyName || 'DEFAULT');

      // Cart items'ı API formatına çevir
      const cartItemsForApi = cart.map((item) => {
        const product = allInventoryItems.find((inv) => inv.id === item.id);
        const warehouseId = resolveWarehouseId(item.id, product?.categoryId, screenSettings);

        if (!warehouseId) {
          throw new Error(`Ürün için depo bilgisi bulunamadı: ${item.name}`);
        }

        // ✅ KDV Dahil/Hariç bilgisi: Sepetteki vatStatus'a göre PriceType belirle
        const priceType = item.vatStatus === 'included' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED';

        return {
          ProductId: item.id,
          ProductName: product?.name || item.name,
          WarehouseId: warehouseId,
          Quantity: item.quantity,
          UnitPrice: item.price,
          VatRate: product?.vatRate || 0,
          SctRate: product?.sctRate || 0,
          // ✅ KDV Dahil/Hariç bilgisi (fatura hesaplaması için)
          PriceType: priceType,
          // ✅ Reçete/Menü bazlı stok düşme için
          HasRecipe: product?.hasRecipe || false,
          HasMenu: product?.hasMenu || false,
          SelectedOptions: (item as any).selectedOptions || null,
          TotalPriceDifference: (item as any).totalPriceDifference || 0,
        };
      });

      // ✅ ATOMİK REQUEST - TEK API ÇAĞRISI (POS + NAKİT + STOK + FATURA)
      // ✅ Restoran modunda Description "Restoran Satışı" olmalı
      const partialDescription = isRestaurantMode ? 'Restoran Satışı (Karma)' : 'Karma Ödeme';
      
      atomicRequest = {
        IdempotencyKey: crypto.randomUUID(),

        CompanyName: companyName || null,
        ScreenId: screenId ? parseInt(screenId.toString(), 10) : null,
        AccountId: parseInt(selectedCustomer.id.toString(), 10),
        AccountName: selectedCustomer.companyName,
        // POS Bilgileri
        BankAccountId: paymentDetails.posOption.bankAccountId,
        BankSubAccountId: paymentDetails.posOption.subAccountId,
        BankAccountName: paymentDetails.posOption.bankAccountName || null, // ✅ DÜZELTME: label değil, gerçek banka adı
        PosAmount: paymentDetails.posAmount,
        // Nakit Bilgileri
        CashboxId: parseInt(cashPaymentCashboxId),
        CashboxName: cashPaymentCashboxName || null, // ✅ YENİ: Gün sonu raporu için kasa adı
        CashAmount: paymentDetails.cashAmount,
        // Ortak
        Currency: 'TRY',
        Description: partialDescription, // ✅ YENİ: Restoran modu için açıklama
        BranchId: userDetails?.branchesId || null, // ✅ YENİ: Gün sonu raporu için kritik!
        BranchName: userDetails?.branchesId ? getBranchName(userDetails.branchesId) : null, // ✅ YENİ: Kasa hareket raporu için şube adı
        IsRestaurantMode: isRestaurantMode, // ✅ YENİ: Restoran modu için RS- prefix
        CashierId: currentCashierId || null, // ✅ YENİ: Kasiyer ID (gün sonu raporu için)
        CashierName: currentCashierName || null, // ✅ YENİ: Kasiyer adı (gün sonu raporu için)
        // POS Detayları
        AuthCode: posFiscalData?.AuthCode,
        BatchNo: posFiscalData?.BatchNo,
        MaskedCardNumber: posFiscalData?.MaskedCardNumber,
        // ✅ PavoPay'de SaleNumber fallback: POS iadesinde ilişkili fatura/gün sonu
        // fişinin backend'de bulunabilmesi için kritik (FiscalTransactionId eşleşmesi).
        FiscalTransactionId: posFiscalData?.FiscalTransactionId || posFiscalData?.SaleNumber,
        CardType: posFiscalData?.CardType,
        CreatedBy: effectiveCreatedBy, // ✅ Kasiyer varsa kasiyer adı kullanılır
        IsFiscalReceipt: screenSettings?.useFiscalDeviceForCash ?? true,
        EInvoiceETTN: posFiscalData?.EInvoiceETTN || null,
        PavoPayInquiryLink: posFiscalData?.PavoPayInquiryLink || null,
        Items: cartItemsForApi,
      };

      console.log('[ATOMİK] 🔄 Karma + Fatura isteği gönderiliyor:', atomicRequest);

      try {
        localStorage.setItem('gts_pending_atomic_payment', JSON.stringify({
          endpoint: 'process-partial-with-invoice',
          request: atomicRequest,
          timestamp: Date.now(),
        }));
      } catch (lsErr) { console.warn('[localStorage] Kayıt başarısız:', lsErr); }

      const atomicResponse = await salesService.processPartialWithInvoice(atomicRequest);

      if (!atomicResponse.Success) {
        throw new Error(`Karma ödeme işlemi başarısız (tüm işlemler geri alındı): ${atomicResponse.Message}`);
      }

      localStorage.removeItem('gts_pending_atomic_payment');

      posReferenceNumber = atomicResponse.ReferenceNumber || '';
      const invoiceNumber = atomicResponse.InvoiceNumber || '';
      
      console.log(`[KARMA ÖDEME] ✅ ATOMİK SQL kaydı başarılı!`);
      console.log(`[KARMA ÖDEME] 📄 Referans: ${posReferenceNumber}`);
      console.log(`[KARMA ÖDEME] 📄 Fatura: ${invoiceNumber}`);

      // 💰 Para çekmecesi tetiği: karma ödemenin nakit parçası başarıyla kaydedildi
      if ((paymentDetails.cashAmount || 0) > 0) {
        try {
          console.log(
            `💰 [CashDrawer@usePartialPayment:cashSuccess] callback çağrılıyor, paidByCash=${paymentDetails.cashAmount}`
          );
          onCashPaymentSuccess?.(paymentDetails.cashAmount);
        } catch (cbErr) {
          console.warn('[usePartialPayment] onCashPaymentSuccess callback hatası:', cbErr);
        }
      }

      // ==================== FİŞ YAZDIRMA ====================
      // ✅ FIX: printReceiptAfterPayment HER ZAMAN çağrılır (nakit/POS akışlarıyla aynı kural).
      // Yazarkasa kullanıldıysa basılıp basılmayacağına fonksiyonun kendi içindeki
      // useBothFiscalAndReceiptPrinter guard'ı karar verir — eskiden buradaki
      // `skipFiscalDevice` koşulu yüzünden karma satışta HS numaralı fiş hiç çıkmıyordu.
      if (printReceiptAfterPayment) {
        console.log(`[KARMA ÖDEME] 🖨️ Fiş yazdırma kontrolü (skipFiscalDevice: ${skipFiscalDevice})...`);
        try {
          const invoiceItems = cart.map((item) => {
            const product = allInventoryItems.find((inv) => inv.id === item.id);
            const vatRate = product?.vatRate || 0;
            const unitPrice = item.price;
            const quantity = item.quantity;
            const lineTotal = unitPrice * quantity;
            const vatAmount = lineTotal * (vatRate / 100);
            const grossTotal = lineTotal + vatAmount;

            return {
              id: `qs-${item.id}-${Date.now()}`,
              itemId: item.id,
              itemName: item.name,
              quantity: quantity,
              unit: product?.unit || 'adet',
              unitPrice: unitPrice,
              vatRate: vatRate,
              sctRate: 0,
              discountRate: 0,
              lineTotal: lineTotal,
              netTotal: lineTotal,
              vatAmount: vatAmount,
              sctAmount: 0,
              grossTotal: grossTotal,
            };
          });

          await printReceiptAfterPayment(
            posReferenceNumber,
            'KARMA ÖDEME (POS + NAKİT)',
            invoiceItems,
            undefined,
            reservedShortCode
          );

          console.log('[KARMA ÖDEME] ✅ Fiş yazdırıldı');
        } catch (printError) {
          console.error('[KARMA ÖDEME] Fiş yazdırma hatası:', printError);
          showSnackbar('Fiş yazdırılırken bir hata oluştu, ancak satış işlemi tamamlandı.', 'warning');
        }
      } else {
        console.log('[KARMA ÖDEME] ⚠️ printReceiptAfterPayment callback tanımlı değil, fiş yazdırılamıyor');
      }

      // ==================== İŞLEM TAMAMLANDI ====================
      if (setPaymentWaitingStatus && setPaymentWaitingMessage && setPaymentWaitingModalOpen) {
        setPaymentWaitingStatus('success');
        setPaymentWaitingMessage('İşlem başarıyla tamamlandı');

        setTimeout(() => {
          setPaymentWaitingModalOpen(false);
        }, 2000);
      }

      const endTime = performance.now();
      console.log(`[KARMA ÖDEME] ⏱️ TOPLAM SÜRE: ${(endTime - startTime).toFixed(2)}ms`);
      console.log(`[KARMA ÖDEME] ✅ TAMAMLANDI - Ref: ${posReferenceNumber}, Fatura: ${invoiceNumber}`);

      // 🍽️ MUTFAK EKRANI ENTEGRASYONU
      // ✅ DÜZELTME: Mutfak ekranı SADECE normal hızlı satış modunda çalışır (KARMA)
      // Restoran modu: Sipariş "Mutfağa Yazdır" butonu ile gönderilir, ödeme alındığında GÖNDERİLMEZ
      // Çünkü restoran modunda müşteri ödeme yaptığında yemeği bitirmiş demektir (A la carte)
      try {
        const useKitchenScreen = screenSettings?.useKitchenScreen || false;
        const selectedQRMenuId = screenSettings?.selectedQRMenuId || null;

        if (useKitchenScreen && selectedQRMenuId && !isRestaurantMode) {
          console.log('[MUTFAK EKRANI] 🍽️ Mutfak ekranı entegrasyonu AKTIF (Normal Hızlı Satış - KARMA)');

          const siparisDetaylari = cart.map(item => {
            const inventoryItem = allInventoryItems.find(inv => inv.id === item.id);
            return {
              UrunId: item.id,
              UrunAdi: item.name,
              BirimFiyat: item.price,
              Miktar: item.quantity,
              ToplamFiyat: item.price * item.quantity,
              UrunResmi: inventoryItem?.imageUrl || null,
              Aciklama: null,
              CategoryId: inventoryItem?.categoryId || null,
              VatRate: inventoryItem?.vatRate || null
            };
          });

          const kitchenOrderRequest = {
    
            QRMenuId: selectedQRMenuId,
            PersonelFirebaseUid: userDetails?.uid || null,
            PersonelName: userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || 'Hızlı Satış' : 'Hızlı Satış',
            ToplamTutar: calculateTotal(),
            SiparisDetaylari: siparisDetaylari,
            SiparisNotu: null,
            OdemeYontemi: 'Karma',
            IslemId: null,
            DekontId: null,
            QuickSaleScreenId: screenId ? parseInt(screenId) : null,
            ReferenceNumber: posReferenceNumber,
            IsRestaurantMode: false,
            ReservedLongCode: reservedLongCode || null,
            ReservedShortCode: reservedShortCode || null,
            BranchId: activeBranchId || null
          };

          const { default: salesService } = await import('../../../../services/salesService');
          const kitchenResponse = await salesService.createKitchenOrderFromQuickSale(kitchenOrderRequest);

          if (kitchenResponse.success) {
            console.log('[MUTFAK EKRANI] ✅ Sipariş gönderildi! Kısa Kod:', kitchenResponse.data?.shortCode);
            showSnackbar(
              `Karma ödeme başarılı!\nRef: ${posReferenceNumber}\nFatura: ${invoiceNumber}\nMutfak: ${kitchenResponse.data?.shortCode}`,
              'success'
            );
          } else {
            showSnackbar(
              `Karma ödeme başarılı!\nRef: ${posReferenceNumber}\nFatura: ${invoiceNumber}`,
              'success'
            );
          }
        } else if (isRestaurantMode && useKitchenScreen && selectedQRMenuId) {
          console.log('[MUTFAK EKRANI] 🍽️ Restoran modu (KARMA) - Mutfak entegrasyonu AKTIF, ödeme sonrası güncelleme/oluşturma...');

          const siparisDetaylari = cart.map(item => {
            const inventoryItem = allInventoryItems.find(inv => inv.id === item.id);
            return {
              UrunId: item.id,
              UrunAdi: item.name,
              BirimFiyat: item.price,
              Miktar: item.quantity,
              ToplamFiyat: item.price * item.quantity,
              UrunResmi: inventoryItem?.imageUrl || null,
              Aciklama: null,
              CategoryId: inventoryItem?.categoryId || null,
              VatRate: inventoryItem?.vatRate || null
            };
          });

          const kitchenOrderRequest = {
            QRMenuId: selectedQRMenuId,
            PersonelFirebaseUid: userDetails?.uid || null,
            PersonelName: userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || 'Restoran Satışı' : 'Restoran Satışı',
            ToplamTutar: calculateTotal(),
            SiparisDetaylari: siparisDetaylari,
            SiparisNotu: null,
            OdemeYontemi: 'Karma',
            IslemId: null,
            DekontId: null,
            QuickSaleScreenId: screenId ? parseInt(screenId) : null,
            ReferenceNumber: posReferenceNumber,
            IsRestaurantMode: true,
            ReservedLongCode: reservedLongCode || null,
            ReservedShortCode: reservedShortCode || null,
            BranchId: activeBranchId || null,
            IsPaymentUpdate: true,
            TableName: tableName || null,
            CustomerAccountName: selectedCustomer?.companyName || null
          };

          console.log('[MUTFAK EKRANI] 📤 Restoran ödeme güncelleme request (KARMA):', kitchenOrderRequest);

          const { default: salesService } = await import('../../../../services/salesService');
          const kitchenResponse = await salesService.createKitchenOrderFromQuickSale(kitchenOrderRequest);

          if (kitchenResponse.success) {
            console.log('[MUTFAK EKRANI] ✅ Restoran mutfak kaydı güncellendi/oluşturuldu (KARMA)');
            console.log('[MUTFAK EKRANI] 📋 KISA KOD:', kitchenResponse.data?.shortCode);
            showSnackbar(
              `Karma ödeme başarılı!\nRef: ${posReferenceNumber}\nFatura: ${invoiceNumber}\nMutfak: ${kitchenResponse.data?.shortCode}`,
              'success'
            );
          } else {
            console.error('[MUTFAK EKRANI] ❌ Restoran mutfak güncelleme başarısız (KARMA):', kitchenResponse.message);
            showSnackbar(
              `Karma ödeme başarılı!\nRef: ${posReferenceNumber}\nFatura: ${invoiceNumber}`,
              'success'
            );
          }
        } else if (isRestaurantMode) {
          console.log('[MUTFAK EKRANI] ℹ️ Restoran modu (KARMA) - Mutfak entegrasyonu kapalı, atlanıyor');
          showSnackbar(
            `Karma ödeme başarılı!\nRef: ${posReferenceNumber}\nFatura: ${invoiceNumber}`,
            'success'
          );
        } else {
          showSnackbar(
            `Karma ödeme başarılı!\nRef: ${posReferenceNumber}\nFatura: ${invoiceNumber}`,
            'success'
          );
        }
      } catch (kitchenError) {
        console.error('[MUTFAK EKRANI] ❌ Hata:', kitchenError);
        showSnackbar(
          `Karma ödeme başarılı!\nRef: ${posReferenceNumber}\nFatura: ${invoiceNumber}`,
          'success'
        );
      }

      // ✅ YENİ: Restoran modunda adisyonu kapat (CompletedSales'e taşı)
      if (isRestaurantMode && tableId) {
        console.log('[KARMA ÖDEME - RESTORAN] 🍽️ Restoran modu aktif, adisyon kapatılıyor...');
        try {
          const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');
          
          // Önce tableId'den activeSaleId'yi al
          const numericTableId = parseInt(tableId);
          const activeSale = await restaurantActiveSaleApi.getByTableId(numericTableId);
          
          if (activeSale && activeSale.Id) {
            console.log('[KARMA ÖDEME - RESTORAN] ✅ Aktif satış bulundu, ID:', activeSale.Id);
            
            // Karma ödeme detaylarını hazırla (gün sonu raporu için kasa/banka bilgisi)
            const partialPaymentDetailsJson = JSON.stringify({
              cashAmount: paymentDetails.cashAmount,
              cashboxId: cashPaymentCashboxId,
              cashboxName: cashPaymentCashboxName || null, // ✅ DÜZELTME: Gerçek kasa adı
              posAmount: paymentDetails.posAmount,
              posLabel: paymentDetails.posOption.label,
              bankAccountId: paymentDetails.posOption.bankAccountId,
              bankSubAccountId: paymentDetails.posOption.subAccountId,
              bankAccountName: paymentDetails.posOption.bankAccountName || null, // ✅ DÜZELTME: label değil, gerçek banka adı
            });
            
            const partialPayments: Array<any> = [];
            if (paymentDetails.cashAmount > 0) {
              partialPayments.push({
                paymentMethod: 'cash',
                amount: paymentDetails.cashAmount,
                cashboxId: cashPaymentCashboxId ? parseInt(cashPaymentCashboxId) : undefined,
                cashboxName: cashPaymentCashboxName || undefined,
              });
            }
            if (paymentDetails.posAmount > 0) {
              partialPayments.push({
                paymentMethod: 'pos',
                amount: paymentDetails.posAmount,
                bankAccountId: paymentDetails.posOption.bankAccountId || undefined,
                bankAccountName: paymentDetails.posOption.bankAccountName || undefined,
                bankSubAccountId: paymentDetails.posOption.subAccountId || undefined,
                bankSubAccountName: paymentDetails.posOption.label || undefined,
              });
            }

            const completeResult = await restaurantActiveSaleApi.completeSale(activeSale.Id, {
              paymentMethod: 'Parçalı (Nakit + POS)',
              paymentDetailsJson: partialPaymentDetailsJson,
              totalAmount: calculateTotal(),
              completedBy: userDetails?.uid || null,
              completedByName: effectiveCreatedBy,
              saleNumber: posReferenceNumber || undefined,
              invoiceNumber: invoiceNumber || undefined,
              referenceNumber: posReferenceNumber || undefined,
              closedByWaiterName: waiterName || undefined,
              isFiscalReceipt: screenSettings?.useFiscalDeviceForCash ?? true,
              payments: partialPayments,
            });
            
            if (completeResult.success) {
              console.log('[KARMA ÖDEME - RESTORAN] ✅ Adisyon başarıyla kapatıldı!', completeResult.data);
            } else {
              console.error('[KARMA ÖDEME - RESTORAN] ❌ Adisyon kapatılamadı:', completeResult.message);
              showSnackbar(`Uyarı: Adisyon kapatılamadı - ${completeResult.message}`, 'warning');
            }
          } else {
            console.log('[KARMA ÖDEME - RESTORAN] ⚠️ Bu masa için aktif satış bulunamadı');
          }
        } catch (restaurantError: any) {
          console.error('[KARMA ÖDEME - RESTORAN] ❌ Restoran adisyon kapatma hatası:', restaurantError);
          showSnackbar('Uyarı: Adisyon kapatılırken hata oluştu, ancak ödeme başarılı.', 'warning');
        }
      }

      clearCart();
      
      // ✅ YENİ: Restoran modundaysa RestaurantScreen'e geri dön
      if (isRestaurantMode && restaurantScreenId && navigateToRestaurantScreen) {
        console.log('[KARMA ÖDEME - RESTORAN] 🔙 Karma ödeme tamamlandı, RestaurantScreen\'e geri dönülüyor...');
        setTimeout(() => {
          navigateToRestaurantScreen();
        }, 500);
      }
    } catch (error: any) {
      console.error('[KARMA ÖDEME] Atomik işlem hatası:', error);

      if (accessLockCode(error)) {
        clearPendingAtomicPayment();
        if (setIsProcessingPosPayment) setIsProcessingPosPayment(false);
        showAlertModal('Süreniz Doldu', accessLockMessage(error), false, null);
        return;
      }

      if (setIsProcessingPosPayment) {
        setIsProcessingPosPayment(false);
      }

      const MAX_RETRY = 3;
      let retryCount = 0;

      const retryFn = async () => {
        retryCount++;
        console.log(`[RETRY] Karma atomik işlem yeniden deneniyor (${retryCount}/${MAX_RETRY})...`);
        try {
          const retryResponse = await salesService.processPartialWithInvoice(atomicRequest!);
          if (!retryResponse.Success) {
            throw new Error(retryResponse.Message || "Karma ödeme kaydı başarısız (retry)");
          }
          console.log("[RETRY] Karma atomik işlem başarılı!", retryResponse.ReferenceNumber);
          localStorage.removeItem('gts_pending_atomic_payment');
          showSnackbar(`Karma Satışı (${retryResponse.ReferenceNumber}) başarıyla kaydedildi.`, 'success');
          clearCart();
          if (isRestaurantMode && restaurantScreenId && navigateToRestaurantScreen) {
            setTimeout(() => navigateToRestaurantScreen(), 500);
          }
        } catch (retryError: any) {
          console.error("[RETRY] Karma atomik işlem yeniden başarısız:", retryError);
          if (accessLockCode(retryError)) {
            clearPendingAtomicPayment();
            showAlertModal('Süreniz Doldu', accessLockMessage(retryError), false, null);
            return;
          }
          const canStillRetry = retryCount < MAX_RETRY;
          showAlertModal(
            "Ödeme Kaydı Hatası",
            `Karma ödeme alındı ancak kayıt yapılamadı.\n\n` +
            `Hata: ${retryError.message}\n\n` +
            (canStillRetry ? `Deneme: ${retryCount + 1}/${MAX_RETRY}\nYeniden deneyebilirsiniz.` : `Maksimum deneme sayısına ulaşıldı. Lütfen sistem yöneticinize başvurun.`),
            false,
            canStillRetry ? retryFn : null
          );
        }
      };

      showAlertModal(
        "Ödeme Kaydı Hatası",
        `Karma ödeme alındı ancak kayıt yapılamadı.\n\n` +
        `Hata: ${error.message}\n\n` +
        `"Yeniden Dene" ile ödeme kaydını tekrar deneyebilirsiniz.\nÖdeme tekrar alınmayacak, sadece kayıt işlemi tekrarlanacaktır.`,
        false,
        retryFn
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    processPartialPayment,
    isProcessing,
    // ✅ YENİ: Yazarkasa bağlı değil onay modalı
    isFiscalDisconnectedConfirmOpenPartial,
    handleFiscalDisconnectedConfirmPartial: () => {
      console.log('[handleFiscalDisconnectedConfirmPartial] Kullanıcı onayladı, işlem devam ediyor...');
      setIsFiscalDisconnectedConfirmOpenPartial(false);
      if (pendingPartialPaymentCallback) {
        pendingPartialPaymentCallback();
      }
      setPendingPartialPaymentCallback(null);
    },
    handleFiscalDisconnectedCancelPartial: () => {
      console.log('[handleFiscalDisconnectedCancelPartial] Kullanıcı iptal etti.');
      setIsFiscalDisconnectedConfirmOpenPartial(false);
      setPendingPartialPaymentCallback(null);
      setIsProcessing(false);
      if (setIsProcessingPosPayment) {
        setIsProcessingPosPayment(false);
      }
    },
  };
};

